import React from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DetailImageAndLabel from 'components/DetailImageAndLabel';
import ResourcePageHighlights from 'components/ResourcePageHighlights';
import ResourceName from 'components/ResourceName';
import ResourceType from 'components/ResourceType';
import ItemPageScoring from 'components/ItemPageScoring';
import PaddedGrid from 'components/PaddedGrid';
import useSiteCopySelector from 'components/useSiteCopySelector';
import ResourceImageAndInfoLayout from 'components/ResourceImageAndInfoLayout';
import ListTitle from 'components/ListTitle';
import ItemPageDescriptionContainer from 'components/ItemPageDescriptionContainer';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import { ButtonBase } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  titleWithIcon: {
    display: 'flex',
  },
  root: {
    position: 'relative',
    marginTop: 60,
    marginBottom: 20,
  },
  typeWrapper: {
    [theme.breakpoints.down('sm')]: {
      paddingTop: 18,
    },
  },
  learnMoreBtn: {
    width: '100%',
    height: 49,
    border: '1px solid #0061A0',
    color: '#0061A0',
    fontFamily: 'MadaSemiBold',
    fontSize: 15,
    margin: '10px 0',
  },
}));

function OrganizationInformation({ data, recommendedResources, relatedNews }) {
  const classes = useStyles();
  const [recommendBotSiteCopy, resourceItemPageSiteCopy] = useSiteCopySelector([
    'recommendbot-a',
    'resource-item-page',
  ]);
  const orgData = _get(data, 'items.0.fields.organization.0');
  const history = useHistory();
  if (_isEmpty(orgData)) return null;
  const {
    sys: { contentType },
    fields: {
      name,
      subtitle,
      audienceType = [],
      highlights = [],
      tags = [],
      newDescription,
      type,
      internalStatusIgnore = [],
      topics,
      slug,
    },
    fields,
  } = orgData;
  const stopRecommendBot = internalStatusIgnore.includes('Stop RecommendBot');

  const defaultParagraph = _get(recommendBotSiteCopy, 'body.json');
  if (
    !stopRecommendBot &&
    defaultParagraph &&
    !newDescription.content.includes(defaultParagraph)
  ) {
    newDescription.content = newDescription.content.concat(defaultParagraph);
  }
  const recommendBotConfig = _get(recommendBotSiteCopy, 'pageCopy');

  const imageNode = (
    <DetailImageAndLabel data={fields} type={contentType.sys.id} />
  );

  const title = _get(
    resourceItemPageSiteCopy,
    'pageCopy.programOrgsHeader',
    '',
  );

  const infoNode = (
    <>
      <PaddedGrid item xs={12}>
        <div className={classes.typeWrapper}>
          <ResourceType
            type={_get(resourceItemPageSiteCopy, [
              'pageCopy',
              'resourceTypeMapping',
              'ORGANIZATION',
            ])}
          />
        </div>
      </PaddedGrid>
      <PaddedGrid item xs={12}>
        <ResourceName name={name} />
      </PaddedGrid>
      {!_isEmpty(subtitle) && (
        <PaddedGrid item xs={12}>
          <Typography color="textPrimary" variant="body2">
            {subtitle}
          </Typography>
        </PaddedGrid>
      )}

      <ResourcePageHighlights
        audience={audienceType}
        highlights={highlights}
        tags={tags}
      />
      <ItemPageScoring data={data.items} type="service" />
    </>
  );
  const handleOrgRedirect = () => {
    history.push(`/organizations/${slug}`);
  };
  return (
    <>
      <PaddedGrid className={classes.root}>
        <ListTitle title={title} />
      </PaddedGrid>
      <ResourceImageAndInfoLayout image={imageNode} info={infoNode} />
      <ItemPageDescriptionContainer>
        <RichTextReactRenderer
          data={newDescription}
          isDescription
          type="name"
          name={name}
          contentType="organization"
          subtype={type}
          extraData={{
            stopRecommendBot,
            recommendBotConfig,
            resourceType: stopRecommendBot
              ? []
              : recommendedResources.filter(el => el.type !== 'Assessments'),
            assessments: stopRecommendBot
              ? []
              : recommendedResources.filter(el => el.type === 'Assessments'),
            topics:
              stopRecommendBot || !topics
                ? []
                : topics.filter(
                    el =>
                      !el.approach &&
                      _get(el, 'fields.reviewStatus') !== 'Internal',
                  ),
            approaches:
              stopRecommendBot || !topics
                ? []
                : topics.filter(el => el.approach),
            news: stopRecommendBot ? [] : relatedNews,
          }}
        />
      </ItemPageDescriptionContainer>
      <ButtonBase onClick={handleOrgRedirect} className={classes.learnMoreBtn}>
        {_get(
          resourceItemPageSiteCopy,
          'pageCopy.serviceOrgLearnMoreHeader',
          '',
        )}
      </ButtonBase>
    </>
  );
}

OrganizationInformation.propTypes = {
  data: PropTypes.array,
  recommendedResources: PropTypes.object,
  relatedNews: PropTypes.object,
};

export default OrganizationInformation;
